import type { ReactNode } from "react";
import React, { useLayoutEffect, useState } from "react";

import {
  Button,
  IconButton,
  Loader,
  showAlert,
  Stack,
  Typography,
} from "@braintrust/braintrust-ui-components";
import { DownloadArrowIcon } from "@braintrust/braintrust-ui-components/Icons";
import { useFreelancerId } from "@js/apps/common/hooks/use-freelancer-id";
import { useGetFreelancerInvoicesQuery } from "@js/apps/invoices/api";
import { W8FormGuidance } from "@js/apps/settings/components";
import { TaxDocumentOverrideButton } from "@js/apps/settings/components/tax-document-override-button";
import {
  openTaxDocumentsModal,
  TaxDocumentsAlert,
} from "@js/apps/settings/components/tax-documents";
import { SectionSubtitle } from "@js/apps/settings/forms/helpers";
import type { FormType } from "@js/apps/settings/hooks/use-tax-documents";
import {
  FORM_TYPE,
  useTaxDocuments,
} from "@js/apps/settings/hooks/use-tax-documents";
import { Datetime } from "@js/components/date";
import type { IsoDateTime } from "@js/utils/date/types";
import { DateTimeFormats } from "@js/utils/date/types";

import { PaymentsPanelItem } from "../payments-panel-item";

export const TaxDocuments = React.memo(() => {
  const { data: invoicesResponse, isLoading: isLoadingInvoices } =
    useGetFreelancerInvoicesQuery({ page: 1 });
  const hasInvoices = !!invoicesResponse?.count;
  // mountAlert flag is needed to show the alert correctly after going to the payments page back and forth
  const [mountAlert, setMountAlert] = useState(false);
  const freelancerId = useFreelancerId();
  const taxDocuments = useTaxDocuments();
  const {
    formTypeName,
    returnForFormType,
    uploadedAt,
    deleteForm,
    uploadedFormUrl,
    country,
    isLoading: isLoadingDocuments,
  } = taxDocuments;

  const isLoading = isLoadingInvoices || isLoadingDocuments;

  useLayoutEffect(() => {
    setMountAlert(true);
  }, []);

  const showOverride = !!formTypeName && !!freelancerId;
  const descriptionContainerProps = showOverride
    ? { sx: { mb: 1 } }
    : undefined;

  return (
    <>
      <SectionSubtitle
        title={formTypeName ?? ""}
        description={
          country
            ? returnForFormType({
                w8: (<W8FormGuidance />) as ReactNode,
                w9: `U.S. residents and citizens must provide their W-9 tax information.`,
              })
            : "Before you can add tax documents, you must set your country location."
        }
        descriptionContainerProps={descriptionContainerProps}
        action={
          country && (
            <Button
              disabled={false}
              variant="secondary"
              onClick={() => openTaxDocumentsModal()}
            >
              Add {formTypeName}
            </Button>
          )
        }
      />
      {!!formTypeName && !!freelancerId && (
        <TaxDocumentOverride formTypeName={formTypeName} />
      )}
      {isLoading && <Loader />}
      {!isLoading && uploadedAt && uploadedFormUrl && (
        <PaymentsPanelItem
          header="Your Form"
          subheader={[
            getSubheader(uploadedFormUrl),
            <React.Fragment key="experience-panel-item-subheader">
              Uploaded at{" "}
              <Datetime
                date={uploadedAt as IsoDateTime}
                format={DateTimeFormats["January 1st 1970, 0:00:00 pm"]}
              />
            </React.Fragment>,
          ]}
          onDelete={hasInvoices ? undefined : () => deleteForm?.()}
          renderButtons={(buttons) => {
            return [
              <IconButton
                aria-label="download form"
                key="download_form"
                variant="tertiary"
                size="small"
                href={uploadedFormUrl}
                download
              >
                <DownloadArrowIcon />
              </IconButton>,
              ...buttons,
            ];
          }}
          borderBottom={true}
        />
      )}
      {mountAlert && showAlert(<TaxDocumentsAlert {...taxDocuments} />)}
    </>
  );
});

const TaxDocumentOverride = ({ formTypeName }: { formTypeName: FormType }) => {
  const formType =
    formTypeName === FORM_TYPE.W8
      ? ENUMS.TaxForm.W8_FORM
      : ENUMS.TaxForm.W9_FORM;

  const switchToName =
    formTypeName === FORM_TYPE.W8 ? FORM_TYPE.W9 : FORM_TYPE.W8;

  return (
    <Stack
      sx={{
        flexDirection: { xs: "column", sm: "row" },
        gap: 1,
        mt: 1,
        mb: { xs: 3, sm: 8 },
        justifyContent: "space-between",
        alignItems: { xs: "flex-start", sm: "center" },
      }}
    >
      <Typography component="p" color="secondary">
        {switchToName === FORM_TYPE.W9
          ? "I am a U.S. citizen working outside of the U.S."
          : "I am not a U.S. citizen and I work outside the U.S."}
      </Typography>
      <TaxDocumentOverrideButton formType={formType} />
    </Stack>
  );
};

const getSubheader = (uploadedFormUrl: string) => {
  const matched = uploadedFormUrl.match(/\.([0-9a-z]+)(?:[?#]|$)/i)?.[1];
  if (matched) {
    return matched.toUpperCase();
  }

  return "";
};
